<template lang="pug" src="./price.pug"></template>

<i18n>
{
    "de": {
        "Gross price": "Brutto-Preis",
        "Net price": "Netto-Preis",
        "Tax rate": "Steuersatz",
        "Regular": "Regulär",
        "Reduced": "Vermindert",
        "Type": "Art",
        "Sale date range": "Angebotszeitraum",
        "Starts At": "Beginnt am",
        "Ends At": "Endet am",
        "min. Quantity": "min. Bestellmenge",
        "pcs.": "stk.",
        "Customer group": "Kundengruppe",
        "Customer group price": "Kundengruppenpreis",
        "Price on request": "Preis auf Anfrage",
        "For this customer group the storefront shows an inquiry form instead of price and cart. The price stays saved.": "Der Shop zeigt dieser Kundengruppe statt Preis und Warenkorb ein Anfrageformular. Der Preis bleibt gespeichert."
    }
}
</i18n>

<script>
import _ from 'lodash';

import pinia from '@/pinia';
import { useSetupStore, useShopProductStore } from '@/stores';

// import videojs from 'video.js'
//
// import store from '@/store'
// import AuthApi from '@/store/api/system/auth';

export default {
    name: 'Price',
    props: {
        form: {
            type: Object,
            required: true
        },
        rules: {
            type: Object,
            required: false
        },
        price: {
            type: Object,
            required: true
        },
        scope: {
            type: String,
            default: 'form' // stepper / form
        },
        deletable: {
            type: Boolean,
            default: true
        },
        disabled: {
            type: Boolean,
            default: false
        },
        hideSpecialPrices: {
            type: Boolean,
            default: false
        },
        hideNetValue: {
            type: Boolean,
            default: false
        },
        // The price was just added by the user: the gross field takes the focus with its
        // default selected, so typing replaces it.
        autofocus: {
            type: Boolean,
            default: false,
        },
    },
    computed: {
        i() {
            return _.findIndex(this.form.prices, price => price.id === this.price.id);
        },
        priceType: {
            get() {
                return this.price.type;
            },
            set(value) {
                this.update(this.price, 'type', value);
            }
        },
        priceGrossValue: {
            get() {
                return this.price.value;
            },
            set(value) {
                this.update(this.price, 'value', parseFloat(value));
            }
        },
        priceNetValue: {
            get() {
                const defaultTax = _.get(this.activeShop, 'defaultTax');
                const defaultReducedTax = _.get(this.activeShop, 'defaultReducedTax');

                const grossValue = this.price.value;

                let taxRate
                if (this.price.tax) {
                    taxRate = _.get(this.price, 'tax.value', 0);
                } else if (this.priceTaxRate === 'regular') {
                    taxRate = _.get(defaultTax, 'value', 0);
                } else if (this.priceTaxRate === 'reduced') {
                    taxRate = _.get(defaultReducedTax, 'value', 0);
                }

                let value = null;
                if (grossValue) value = _.round((grossValue * 100) / (100 + taxRate), 2);

                return value;
            },
            set(value) {
                const defaultTax = _.get(this.activeShop, 'defaultTax');
                const defaultReducedTax = _.get(this.activeShop, 'defaultReducedTax');

                let taxRate
                if (this.price.tax) {
                    taxRate = _.get(this.price, 'tax.value', 0);
                } else if (this.priceTaxRate === 'regular') {
                    taxRate = _.get(defaultTax, 'value', 0);
                } else if (this.priceTaxRate === 'reduced') {
                    taxRate = _.get(defaultReducedTax, 'value', 0);
                }

                if (value) value = _.round((value / 100) * (100 + taxRate), 2);
                this.update(this.price, 'value', parseFloat(value));
            }
        },
        priceTaxRate: {
            get() {
                let taxRate = this.price.tax_rate;
                if (!taxRate) {
                    if (this.options.tax.items.filter(el => {
                        return el.id === _.get(this.price, 'tax.id') && el.default === true && el.reduced === false
                    }).length) {
                        taxRate = 'regular'
                    }
                    if (this.options.tax.items.filter(el => {
                        return el.id === _.get(this.price, 'tax.id') && el.default === true && el.reduced === true
                    }).length) {
                        taxRate = 'reduced'
                    }
                }
                return taxRate
            },
            set(value) {
                this.form.prices.forEach(price => {
                    this.update(price, 'tax_rate', value);
                    this.update(price, 'tax', null);
                });
            }
        },
        priceTax: {
            get() {
                if (this.priceTaxRate) {
                    return {
                        id: this.priceTaxRate,
                        reduced: this.priceTaxRate === 'reduced',
                        label: this.$t(this.priceTaxRate.charAt(0).toUpperCase() + this.priceTaxRate.slice(1))
                    }
                } else {
                    return this.price.tax;
                }
                // const tax = _.get(this.price, 'tax');
                // return tax || _.get(this.activeShop, 'defaultTax');
            },
            set(tax) {
                switch (tax.id) {
                    case 'none':
                    case 'regular':
                    case 'reduced':
                        this.priceTaxRate = tax.id
                        break;

                    default:
                        this.priceTaxRate = null
                        this.form.prices.forEach(price => {
                            this.update(price, 'tax', tax);
                        });
                        break;
                }
            }
        },
        priceCustomerGroup: {
            get() {
                return this.price.customerGroup;
            },
            set(value) {
                this.update(this.price, 'customerGroup', value);
            }
        },
        // "Preis auf Anfrage" belongs to a regular price of a product or an article: the
        // attribute and shipping method price forms extend this component and switch the
        // checkbox off (the API keeps the flag off their prices as well).
        showPriceOnRequest() {
            return this.price.type !== 'sale';
        },
        // "Preis auf Anfrage" per customer group: the storefront hides this group's price
        // and the cart and offers an inquiry form instead.
        priceOnRequest: {
            get() {
                return _.get(this.price, 'price_on_request', false) === true;
            },
            set(value) {
                this.update(this.price, 'price_on_request', !!value);
            }
        },
        priceDaterange: {
            get() {
                const start = _.get(this.price, 'start_at');
                const start_at = start !== null ? new Date(start * 1000) : '';

                const end = _.get(this.price, 'end_at');
                const end_at = end !== null ? new Date(end * 1000) : '';

                return [start_at, end_at];
            },
            set(value) {
                const start = _.get(value, 0);
                const start_at = start !== undefined ? Math.round(start.getTime() / 1000) : null;

                const end = _.get(value, 1);
                const end_at = end !== undefined ? Math.round(end.getTime() / 1000) : null;

                this.update(this.price, 'start_at', start_at);
                this.update(this.price, 'end_at', end_at);
            }
        },
        priceMinQty: {
            get() {
                return this.price.min_qty;
            },
            set(value) {
                this.update(this.price, 'min_qty', parseInt(value));
            }
        },
        customerGroup() {
            const customerGroup = _.get(this.price, 'customerGroup');
            return customerGroup || _.get(this.activeShop, 'defaultCustomerGroup');
        },
        currency() {
            // get from default group of active shop config
            const sign = _.get(this.customerGroup, 'currency.sign');
            const code = _.get(this.customerGroup, 'currency.code');

            return sign || code;
        },
        taxOptions() {
            let options = []
            if (this.options.tax.items.filter(el => el.default === true && el.reduced === false).length) {
                options.push({
                    id: 'regular',
                    reduced: false,
                    label: this.$t('Regular')
                })
            }
            if (this.options.tax.items.filter(el => el.default === true && el.reduced === true).length) {
                options.push({
                    id: 'reduced',
                    reduced: true,
                    label: this.$t('Reduced')
                })
            }
            const percentages = this.options.tax.items.filter(el => el.default !== true)
            return [...options, ...percentages]
        },
        typeOptions() {
            let types = _.filter(this.options.priceType.items, item => item.value !== 'main');
            types = _.cloneDeep(types);

            if (this.customerGroup && this.price.type !== 'group') {
                types.map(type => {
                    if (type.value === 'group')
                        type.disabled = _.includes(this.usedCustomerGroupIDs, this.customerGroup.id)
                            || this.customerGroup.default;
                });
            }

            return types;
        },
        // customerGroupOptions() {
        //     if (this.price.type === 'group') {
        //         return this.options.customerGroup.items.filter((group) => {
        //             return group.id === this.customerGroup.id;
        //         });
        //     } else {
        //         return this.options.customerGroup.items;
        //     }
        // },
        customerGroupOptions() {
            return this.options.customerGroup.items;
        },
        usedCustomerGroupIDs() {
            const customerGroupPrices = this.form.prices
                .filter(el => el.type === 'group' && el.customerGroup != null);

            let usedCustomerGroupIDs = customerGroupPrices.map(el => el.customerGroup.id);
            if (this.price.type === 'group' && this.customerGroup) {
                usedCustomerGroupIDs = usedCustomerGroupIDs.filter(id => id !== this.customerGroup.id);
            }

            return usedCustomerGroupIDs;
        },
        disabledCustomerGroups() {
            const disabledCount = _.filter(this.customerGroupOptions, customerGroup =>
                this.isCustomerGroupDisabled(customerGroup)
            ).length;

            return this.price.type === 'main' || disabledCount >= this.customerGroupOptions.length - 1;
        },
        activeShop() {
            return this.activeCms.shop;
        },
        setupStore() {
            return useSetupStore();
        },
        shopProductStore() {
            return useShopProductStore(pinia);
        },
        activeCms() {
            return this.setupStore.activeCms;
        },
        options() {
            return this.shopProductStore.options;
        }
    },
    mounted() {
        if (this.autofocus) this.focusGrossValue();
    },
    methods: {
        focusGrossValue() {
            this.$nextTick(() => {
                const input = this.$refs.grossValueInput;
                if (!input) return;
                if (typeof input.focus === 'function') input.focus();
                if (typeof input.select === 'function') input.select();
            });
        },
        isCustomerGroupDisabled(customerGroup) {
            if (this.price.type !== 'group') return false;

            return _.includes(this.usedCustomerGroupIDs, customerGroup.id) || customerGroup.default === true;
        },
        customerGroupLabel(customerGroup) {
            return customerGroup.name;
        },
        remove() {
            if (this.deletable || this.price.type !== 'main') {
                if (this.scope === 'stepper') {
                    this.form.prices.splice(this.i, 1);
                    this.rules.prices.splice(this.i, 1);
                } else if (this.scope === 'form') {
                    this.deletePrice({ index: this.i });
                }
            }
        },
        update(price, field, value) {
            if (this.scope === 'stepper') {
                this.$set(price, field, value)
            } else if (this.scope === 'form') {
                this.updatePrice({ price, field, value });
            }
        },
        deletePrice(payload) {
            return this.shopProductStore.deleteFormPrice(payload);
        },
        updatePrice(payload) {
            return this.shopProductStore.updateFormPrice(payload);
        }
    }
};
</script>

<style lang="sass" scoped>
.el-main.main-slot .from-to
    padding-top: 0
.qty
    max-width: 240px

.is-deletable
    padding-right: 50px !important

.deletable-row
    button.delete-row
        right: 10px
        top: 27px
.price
    // position: relative
    // margin-bottom: 30px
    // padding: 5px 12px
    // background-color: rgba($--color-primary, .15)
    // border-radius: 4px
    // border-left: 4px solid rgba($color-grey-text, .2)
    // transition: border-color .15s ease
    .type-col
        width: 240px
    .customer-group-col
        width: 30%
    .price-on-request-col
        white-space: nowrap
        flex-shrink: 0
        .price-on-request-hint
            margin-left: 6px
            color: $color-grey-text
            cursor: help
            outline: none
            &:hover, &:focus-visible
                color: $--color-primary
    // "Preis auf Anfrage" chosen: no layout change, the amount fields get a light blue
    // tint and the label stands out; the amounts stay editable (the system is built on prices)
    &.is-on-request
        .gross-value-col, .net-value-col, .tax-col
            ::v-deep .el-input__inner
                background-color: rgba($--color-primary, .06)
                border-color: rgba($--color-primary, .4)
        .price-on-request-col .el-checkbox__label
            font-weight: bold
            color: $--color-primary
</style>
